import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import {
  Building2, Users, Receipt, DollarSign, Globe2, Server,
  FileText, FolderKanban, ArrowUpRight, TrendingUp, Trophy, TrendingDown,
  BarChart3, Search as SearchIcon, MapPinned, Share2,
} from "lucide-react";

const Kpi = ({ icon: Icon, label, value, hint, tone = "blue", testid, to }) => {
  const inner = (
    <div className="kpi-card group h-full flex flex-col" data-testid={testid}>
      <div className="flex items-center justify-between mb-6">
        <div className="overline">{label}</div>
        <div className={`w-8 h-8 grid place-items-center rounded-md bg-${tone}-500/10 text-${tone}-400`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <div className="stat-num text-zinc-100">{value}</div>
      {hint && <div className="mt-2 text-xs text-zinc-500 flex items-center gap-1">{hint}</div>}
    </div>
  );
  return to ? <Link to={to} className="block hover:border-blue-500/40 transition-colors">{inner}</Link> : inner;
};

function DeltaChip({ value }) {
  const positive = value >= 0;
  const Icon = positive ? TrendingUp : TrendingDown;
  const cls = positive ? "text-emerald-400 bg-emerald-500/10" : "text-rose-400 bg-rose-500/10";
  return (
    <div className={`mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold ${cls}`}>
      <Icon className="w-3 h-3" /> {positive ? "+" : ""}{value}% vs last week
    </div>
  );
}

function ModuleCard({ testid, icon: Icon, label, hint, to, tone = "blue" }) {
  const toneMap = {
    blue: "text-blue-400 bg-blue-500/10 border-blue-500/30 hover:border-blue-400/60",
    emerald: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30 hover:border-emerald-400/60",
    amber: "text-amber-400 bg-amber-500/10 border-amber-500/30 hover:border-amber-400/60",
    pink: "text-pink-400 bg-pink-500/10 border-pink-500/30 hover:border-pink-400/60",
  };
  const cls = toneMap[tone] || toneMap.blue;
  return (
    <Link to={to} data-testid={testid} className={`group rounded-xl border bg-slate-900/40 p-4 transition-colors ${cls.split(" ").filter(c => c.startsWith("border") || c.startsWith("hover:border")).join(" ")}`}>
      <div className="flex items-center gap-3 mb-2">
        <div className={`w-9 h-9 grid place-items-center rounded-md ${cls.split(" ").filter(c => c.startsWith("bg-") || c.startsWith("text-")).join(" ")}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="text-sm font-semibold text-slate-100 truncate">{label}</div>
      </div>
      <div className="text-[11px] text-slate-500">{hint}</div>
      <div className="mt-2 text-[11px] text-slate-400 group-hover:text-slate-200 flex items-center gap-1">Open <ArrowUpRight className="w-3 h-3" /></div>
    </Link>
  );
}

export default function Dashboard() {
  const { t } = useLang();
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [won, setWon] = useState(null);
  useEffect(() => { api.get("/dashboard/overview").then(({ data }) => setD(data)).catch(() => setD({})); }, []);
  useEffect(() => {
    api.get("/dashboard/won-leads-week").then(({ data }) => setWon(data)).catch(() => setWon(null));
  }, []);
  const currency = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);
  const hs = d?.hosting_status || { total: 0, active: 0, warning: 0, down: 0 };

  return (
    <DashboardLayout title={t("dashboard.title")} subtitle={t("dashboard.welcome")}>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="dashboard-kpis">
        <Kpi testid="kpi-clients" tone="blue" icon={Users} label={t("dashboard.total_clients")} value={d?.total_clients ?? "—"} to="/app/crm" />
        <Kpi testid="kpi-subs" tone="cyan" icon={Receipt} label={t("dashboard.active_subs")} value={d?.active_subscriptions ?? "—"} to="/app/subscriptions" />
        <Kpi testid="kpi-revenue" tone="emerald" icon={DollarSign} label={t("dashboard.monthly_revenue")} value={currency(d?.monthly_revenue)} />
        <Kpi testid="kpi-domains" tone="amber" icon={Globe2} label={t("dashboard.domains_expiring")} value={d?.domains_expiring ?? "—"} to="/app/domains" hint={d?.domains_expiring > 0 ? `within 30 days` : null} />
        <Kpi testid="kpi-hosting" tone="sky" icon={Server} label={t("dashboard.hosting_status")}
             value={`${hs.active}/${hs.total || 0}`}
             hint={hs.warning || hs.down ? `${hs.warning} warn · ${hs.down} down` : null} to="/app/hosting" />
        <Kpi testid="kpi-invoices" tone="rose" icon={FileText} label={t("dashboard.pending_invoices")} value={d?.pending_invoices ?? "—"} to="/app/invoices" />
        <Kpi testid="kpi-projects" tone="violet" icon={FolderKanban} label={t("dashboard.open_projects")} value={d?.open_projects ?? "—"} to="/app/projects" />
        <Kpi testid="kpi-productivity" tone="teal" icon={TrendingUp} label={t("dashboard.team_productivity")}
             value={(d?.team_productivity || []).reduce((s, x) => s + (x.count || 0), 0)} hint="tasks done · 30d" to="/app/tasks" />
      </div>

      {won && (
        <div className="mt-8 rounded-xl border border-blue-500/40 bg-gradient-to-br from-blue-500/10 via-slate-900/40 to-cyan-500/5 p-6" data-testid="won-leads-widget">
          <div className="flex items-start justify-between flex-wrap gap-4 mb-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 grid place-items-center rounded-md bg-blue-500/15 text-blue-300"><Trophy className="w-5 h-5" /></div>
              <div>
                <div className="text-xs uppercase tracking-widest text-blue-300">Won Leads · This Week</div>
                <div className="text-sm text-slate-400">Monday {new Date(won.week_start).toLocaleDateString()} → today</div>
              </div>
            </div>
            <Link to="/app/leads" data-testid="won-leads-view-all" className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1">View all leads <ArrowUpRight className="w-3 h-3" /></Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="rounded-lg bg-slate-950/50 border border-slate-800 p-4" data-testid="won-leads-total">
              <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Won this week</div>
              <div className="text-3xl font-bold text-slate-100">{won.total_won}</div>
              <DeltaChip value={won.delta_count_percent} />
            </div>
            <div className="rounded-lg bg-slate-950/50 border border-slate-800 p-4" data-testid="won-leads-revenue">
              <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Estimated revenue</div>
              <div className="text-3xl font-bold text-emerald-400">{currency(won.revenue)}</div>
              <DeltaChip value={won.delta_revenue_percent} />
            </div>
            <div className="rounded-lg bg-slate-950/50 border border-slate-800 p-4" data-testid="won-leads-prev">
              <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Previous week</div>
              <div className="text-lg font-semibold text-slate-300">{won.total_prev} won · {currency(won.revenue_prev)}</div>
            </div>
          </div>
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Latest 5 conversions</div>
          <div className="divide-y divide-slate-800/70" data-testid="won-leads-latest">
            {(won.latest_5 || []).map((l) => (
              <Link key={l.lead_id} to={`/app/leads/${l.lead_id}`} className="flex items-center justify-between py-2.5 hover:bg-slate-900/40 -mx-2 px-2 rounded" data-testid={`won-lead-${l.lead_id}`}>
                <div>
                  <div className="text-sm text-slate-100">{l.name}{l.company ? <span className="text-slate-500"> · {l.company}</span> : null}</div>
                  <div className="text-xs text-slate-500">{l.service_needed || "—"} · budget {l.budget || "—"}</div>
                </div>
                <div className="text-[10px] uppercase tracking-widest text-emerald-400">Won</div>
              </Link>
            ))}
            {(!won.latest_5 || won.latest_5.length === 0) && (
              <div className="text-xs text-slate-500 py-6 text-center">No leads converted this week yet.</div>
            )}
          </div>
        </div>
      )}

      {/* Module launchers — one-click into the sales, marketing and reporting surfaces */}
      <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="dashboard-module-cards">
        <ModuleCard testid="module-reports" icon={BarChart3} label="Reports" hint="Sales, revenue & funnel" to="/app/reports" tone="blue" />
        <ModuleCard testid="module-seo" icon={SearchIcon} label="SEO tracker" hint="Keyword rankings" to="/app/reports?tab=seo" tone="emerald" />
        <ModuleCard testid="module-gbp" icon={MapPinned} label="Google Business" hint="Posts & reviews" to="/app/reports?tab=gbp" tone="amber" />
        <ModuleCard testid="module-social" icon={Share2} label="Social scheduler" hint="Post queue" to="/app/content" tone="pink" />
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-6" data-testid="recent-projects-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-sm font-semibold text-zinc-100">{t("dashboard.recent_projects")}</h3>
            <Link to="/app/projects" className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1">{t("dashboard.view_all")} <ArrowUpRight className="w-3 h-3" /></Link>
          </div>
          <div className="divide-y divide-zinc-800">
            {(d?.recent_projects || []).map((p) => (
              <div key={p.project_id} className="flex items-center justify-between py-3">
                <div>
                  <div className="text-sm text-zinc-100">{p.name}</div>
                  <div className="text-xs text-zinc-500">{p.kind}</div>
                </div>
                <div className="text-[10px] uppercase tracking-widest text-blue-400">{p.status}</div>
              </div>
            ))}
            {(!d?.recent_projects || d.recent_projects.length === 0) && (
              <div className="text-xs text-zinc-500 py-6 text-center">{t("common.no_data")}</div>
            )}
          </div>
        </div>

        <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-6" data-testid="recent-invoices-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-sm font-semibold text-zinc-100">{t("dashboard.recent_invoices")}</h3>
            <Link to="/app/invoices" className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1">{t("dashboard.view_all")} <ArrowUpRight className="w-3 h-3" /></Link>
          </div>
          <div className="divide-y divide-zinc-800">
            {(d?.recent_invoices || []).map((i) => (
              <div key={i.invoice_id} className="flex items-center justify-between py-3">
                <div>
                  <div className="text-sm text-zinc-100">{i.title}</div>
                  <div className="text-xs text-zinc-500">{currency(i.total)}</div>
                </div>
                <div className="text-[10px] uppercase tracking-widest text-blue-400">{i.status}</div>
              </div>
            ))}
            {(!d?.recent_invoices || d.recent_invoices.length === 0) && (
              <div className="text-xs text-zinc-500 py-6 text-center">{t("common.no_data")}</div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
